"use client";
import { useState, type ReactNode } from "react";
import Link from "next/link";
import { KeyRound, ArrowRight, ShieldCheck, UserRound } from "lucide-react";
import { Button, Facts, Fact, StatusTag } from ".";
import { useSession } from "../../lib/session";
import { formatTime } from "../../lib/api";
import { GROUPS, roleLabel } from "../console-shell";
import { useRead, ReadState } from "./record-tools";
import {
  BASIS_LABEL,
  GRANT_LABEL,
  GRANT_TONE,
  grantState,
  scopeText,
  REVOKE_LABEL,
  type Grant,
  type Account,
  type AccessEditor
} from "./access-model";
export function AccessNav({ current }: { current: string }) {
  const { hasRole } = useSession();
  return (
    <nav className="access-nav" aria-label="账号与授权工作区">
      {(hasRole("ACCESS_ADMIN")
        ? [
            ["accounts", "账号管理"],
            ["grants", "授权管理"],
            ["authority", "我的授权"]
          ]
        : [["authority", "我的授权"]]
      ).map(([path, label]) => (
        <Link key={path} href={`/${path}`} aria-current={current === path ? "page" : undefined}>
          {label}
        </Link>
      ))}
    </nav>
  );
}
export function GrantStatus({ grant, now }: { grant: Grant; now?: number }) {
  const state = grantState(grant, now);
  return <StatusTag tone={GRANT_TONE[state]}>{GRANT_LABEL[state]}</StatusTag>;
}
export function GrantFacts({ grant: g }: { grant: Grant }) {
  return (
    <Facts>
      <Fact label="被授权人">
        {g.accountName}
        {g.accountStatus === "DISABLED" ? "（账号已停用）" : ""}
      </Fact>
      <Fact label="职责">{roleLabel(g.roleCode)}</Fact>
      <Fact label="组织范围">{scopeText(g)}</Fact>
      <Fact label="约定生效">{formatTime(g.validFrom)}</Fact>
      <Fact label="到期时间">{g.validTo ? formatTime(g.validTo) : "未设到期时间"}</Fact>
      <Fact label="授权依据">{BASIS_LABEL[g.grantBasis]}</Fact>
      <Fact label="签发人">{g.issuerName ?? "未记录签发人"}</Fact>
      {g.appointment && (
        <Fact label="依据任职">
          {g.appointment.personName} · {g.appointment.termName} · {g.appointment.postLabel}
        </Fact>
      )}
    </Facts>
  );
}
export function GrantReading({
  grant: g,
  own = false,
  onAction
}: {
  grant: Grant;
  own?: boolean;
  onAction: (e: AccessEditor) => void;
}) {
  const { whoami, hasRole } = useSession(),
    state = grantState(g),
    mine = g.accountId === whoami?.accountId;
  const entries = GROUPS.flatMap((group) => group.items).filter((item) => (item.roles ?? [item.role]).includes(g.roleCode));
  return (
    <>
      <div className="record-sheet-body access-reading">
        <section className="record-section">
          <div className="record-section-title">
            <h2>{roleLabel(g.roleCode)}</h2>
            <GrantStatus grant={g} />
          </div>
          <GrantFacts grant={g} />
          {g.accountStatus === "DISABLED" && (
            <p className="record-muted">账号已停用，不能以此账号登录办理；授权记录继续保留。</p>
          )}
        </section>
        <section className="record-section">
          <h3>复核与留存</h3>
          <div className="access-history">
            <article>
              <ShieldCheck size={16} />
              <div>
                <strong>
                  {g.reviewStatus === "PENDING_REVIEW"
                    ? "尚待复核"
                    : g.reviewStatus === "REVIEWED"
                      ? "已经复核"
                      : "按既有规则无需另行复核"}
                </strong>
                <p>
                  {g.reviewerName
                    ? `${g.reviewerName}${g.reviewedAt ? ` · ${formatTime(g.reviewedAt)}` : ""}`
                    : g.reviewStatus === "PENDING_REVIEW"
                      ? "由签发人以外的授权管理员核对。"
                      : "沿用本条授权的办理规则。"}
                </p>
              </div>
            </article>
            {g.revokedAt && (
              <article>
                <KeyRound size={16} />
                <div>
                  <strong>已撤销 · {formatTime(g.revokedAt)}</strong>
                  <p>
                    {REVOKE_LABEL[g.revokedReason ?? ""] ?? g.revokedReason ?? "未记录撤销理由"}
                  </p>
                </div>
              </article>
            )}
          </div>
          {g.grantBasis === "DELEGATION" && (
            <p className="record-muted">
              此条由上游持有人转授；不能再次转授，上游授权撤销时一并失效。
            </p>
          )}
        </section>
        {mine && entries.length > 0 && (
          <section className="record-section">
            <h3>对应工作区</h3>
            <div className="access-work-links">
              {entries.map((entry) =>
                state === "ACTIVE" && hasRole(g.roleCode) ? (
                  <Link key={entry.href} className="record-link" href={entry.href}>
                    {entry.label}
                    <ArrowRight size={14} />
                  </Link>
                ) : (
                  <span key={entry.href}>{entry.label}</span>
                )
              )}
            </div>
            <p className="record-muted">具体事项按这条授权的组织范围和当前状态办理。</p>
          </section>
        )}
      </div>
      <div className="access-detail-footer">
        <span>
          {state === "PENDING"
            ? own
              ? "等待授权管理员复核"
              : g.grantedByAccountId === whoami?.accountId
                ? "请其他授权管理员复核"
                : "核对授权依据后办理复核"
            : GRANT_LABEL[state]}
        </span>
        <div className="workspace-actions">
          {own && mine && state === "ACTIVE" && g.grantBasis !== "DELEGATION" && (
            <Button
              onClick={() => onAction({ kind: "grant", action: "delegate", record: g, own: true })}
            >
              转授给经办人
            </Button>
          )}
          {!own && hasRole("ACCESS_ADMIN") && state === "ACTIVE" && <Button variant="outline" onClick={() => onAction({ kind: "grant", action: "shorten", record: g })}>缩短有效期</Button>}
          {!own && state === "PENDING" && g.grantedByAccountId !== whoami?.accountId && (
            <Button onClick={() => onAction({ kind: "grant", action: "review", record: g })}>
              复核授权
            </Button>
          )}
          {!g.revokedAt && (!own || !mine) && (
            <Button
              variant="outline"
              onClick={() => onAction({ kind: "grant", action: "revoke", record: g, own })}
            >
              {own ? "收回转授" : "撤销授权"}
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
export function AccountReading({
  account: a,
  onAction,
  afterCreate
}: {
  account: Account;
  onAction: (e: AccessEditor) => void;
  afterCreate?: ReactNode;
}) {
  const { whoami } = useSession();
  const [tab, setTab] = useState("account");
  const grants = useRead<{ items: Grant[] }>(
    tab === "grants" ? `/api/v1/grants/workspace?accountId=${encodeURIComponent(a.id)}` : null
  );
  return (
    <>
      <nav className="record-tabs" aria-label="账号详情">
        <button
          aria-current={tab === "account" ? "page" : undefined}
          onClick={() => setTab("account")}
        >
          账号与登录
        </button>
        <button
          aria-current={tab === "grants" ? "page" : undefined}
          onClick={() => setTab("grants")}
        >
          授权记录
        </button>
      </nav>
      <div className="record-sheet-body access-reading">
        {tab === "account" ? (
          <>
            <section className="record-section">
              <div className="access-person">
                <span>
                  <UserRound size={24} />
                </span>
                <div>
                  <h2>{a.displayName}</h2>
                  <p>
                    {a.status === "ACTIVE" ? "账号在用" : "账号已停用"}
                    {a.id === whoami?.accountId ? " · 当前登录账号" : ""}
                  </p>
                </div>
              </div>
              <Facts>
                <Fact label="建立时间">{formatTime(a.recordedAt)}</Fact>
                <Fact label="当前生效授权">{a.activeGrantCount} 条</Fact>
                <Fact label="可用登录方式">{a.identities.length} 种</Fact>
              </Facts>
              {afterCreate}
            </section>
            <section className="record-section">
              <div className="record-section-title">
                <h3>登录方式</h3>
                {a.status === "ACTIVE" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onAction({ kind: "account", action: "bind", record: a })}
                  >
                    新增登录方式
                  </Button>
                )}
              </div>
              {a.identities.length === 0 && (
                <p className="record-muted">当前没有可用的登录方式，需新增后才能登录。</p>
              )}
              <ul className="access-identities">
                {a.identities.map((i) => (
                  <li key={i.id}>
                    <KeyRound size={18} />
                    <div>
                      <strong>{i.usernameDisplay ?? "未记录登录名"}</strong>
                      <small>{i.provider === "LOCAL" ? "账号口令登录" : i.provider}</small>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        onAction({ kind: "account", action: "unbind", record: a, identity: i })
                      }
                    >
                      解绑
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          </>
        ) : (
          <section className="record-section">
            <div className="record-section-title">
              <h3>本账号的授权记录</h3>
              <Link className="record-link" href={`/grants?account=${encodeURIComponent(a.id)}`}>
                在授权管理中查看
                <ArrowRight size={14} />
              </Link>
            </div>
            <ReadState {...grants} retry={grants.reload} />
            {grants.data?.items.length === 0 && <p className="record-muted">尚未签发授权。</p>}
            {grants.data?.items.map((g) => (
              <article className="access-mini-grant" key={g.id}>
                <div>
                  <strong>{roleLabel(g.roleCode)}</strong>
                  <GrantStatus grant={g} />
                </div>
                <p>{scopeText(g)}</p>
                <p>
                  {BASIS_LABEL[g.grantBasis]} ·{" "}
                  {g.validTo ? `${formatTime(g.validTo)} 到期` : "未设到期时间"}
                </p>
                <Link
                  className="record-link"
                  href={`/grants?account=${encodeURIComponent(a.id)}&record=${encodeURIComponent(g.id)}`}
                >
                  查看依据与办理
                  <ArrowRight size={14} />
                </Link>
              </article>
            ))}
          </section>
        )}
      </div>
      <div className="access-detail-footer">
        <span>{a.status === "ACTIVE" ? "账号与业务授权分别管理" : "原办理事项继续保留"}</span>
        {a.status !== "ACTIVE" ? (
          <Button
            variant="outline"
            onClick={() => onAction({ kind: "account", action: "enable", record: a })}
          >
            启用账号
          </Button>
        ) : (
          a.id !== whoami?.accountId && (
            <Button
              variant="outline"
              onClick={() => onAction({ kind: "account", action: "disable", record: a })}
            >
              停用账号
            </Button>
          )
        )}
      </div>
    </>
  );
}
